import React, { useState, useEffect, useCallback } from 'react';
import { fetchApi } from '@/utils/fetchApi';

/* ────────────────────────────────────────────────────────────── */
/*  Types                                                        */
/* ────────────────────────────────────────────────────────────── */

interface OnlineBookingConfig {
  is_enabled: boolean;
  booking_mode: 'all_free' | 'fixed_schedule' | 'manual' | 'disabled';
  slot_duration: number;
  max_advance_days: number;
  min_advance_hours: number;
  buffer_between_slots: number;
  page_title: string;
  welcome_message: string;
  booking_instructions: string;
  primary_color: string;
  auto_confirm: boolean;
  require_phone: boolean;
  require_email: boolean;
  confirmation_message: string;
}

interface WeeklySlot {
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface Treatment {
  id: string;
  name: string;
  duration: number;
}

interface ConfigResponse {
  config: OnlineBookingConfig;
  weekly_slots: WeeklySlot[];
  treatments: { available: Treatment[]; all: Treatment[] };
}

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const BOOKING_MODES: { value: string; label: string; desc: string }[] = [
  { value: 'all_free', label: 'All Free Slots', desc: 'Every free slot from working hours is bookable online' },
  { value: 'fixed_schedule', label: 'Fixed Weekly Schedule', desc: 'Define specific weekly windows for online booking' },
  { value: 'manual', label: 'Manual Selection', desc: 'Manually manage which slots appear online' },
  { value: 'disabled', label: 'Disabled', desc: 'Online booking is turned off' },
];

/* ────────────────────────────────────────────────────────────── */
/*  Component                                                    */
/* ────────────────────────────────────────────────────────────── */

const OnlineBookingSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [config, setConfig] = useState<OnlineBookingConfig>({
    is_enabled: false,
    booking_mode: 'all_free',
    slot_duration: 30,
    max_advance_days: 30,
    min_advance_hours: 2,
    buffer_between_slots: 0,
    page_title: '',
    welcome_message: '',
    booking_instructions: '',
    primary_color: '#0ea5e9',
    auto_confirm: true,
    require_phone: true,
    require_email: false,
    confirmation_message: '',
  });
  const [weeklySlots, setWeeklySlots] = useState<WeeklySlot[]>([]);
  const [allTreatments, setAllTreatments] = useState<Treatment[]>([]);
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useState<Set<string>>(new Set());

  /* ── Load ─────────────────────────────────────────────── */
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchApi('appointments/api/online-booking/');
      const data = (res as any) as ConfigResponse;
      if (data.config) setConfig(data.config);
      if (data.weekly_slots) setWeeklySlots(data.weekly_slots);
      if (data.treatments) {
        setAllTreatments(data.treatments.all || []);
        setSelectedTreatmentIds(new Set((data.treatments.available || []).map((t) => t.id)));
      }
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message || 'Failed to load online booking configuration' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  /* ── Save ─────────────────────────────────────────────── */
  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload = {
        ...config,
        treatment_ids: Array.from(selectedTreatmentIds),
        weekly_slots: weeklySlots,
      };
      await fetchApi('appointments/api/online-booking/update/', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setMessage({ type: 'success', text: 'Online booking settings saved successfully' });
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  /* ── Weekly slot helpers ──────────────────────────────── */
  const addWeeklySlot = () => {
    setWeeklySlots((prev) => [...prev, { day_of_week: 0, start_time: '09:00', end_time: '17:00', is_active: true }]);
  };
  const removeWeeklySlot = (idx: number) => {
    setWeeklySlots((prev) => prev.filter((_, i) => i !== idx));
  };
  const updateWeeklySlot = (idx: number, field: keyof WeeklySlot, value: any) => {
    setWeeklySlots((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  /* ── Tenant URL ───────────────────────────────────────── */
  const tenantConfig = (window as any).DJANGO_CONFIG;
  const subdomain = tenantConfig?.tenant?.subdomain || '';
  const baseHost = window.location.host.replace(`${subdomain}.`, '');
  const bookingUrl = subdomain ? `${window.location.protocol}//${subdomain}.${baseHost}/booking/` : `${window.location.origin}/booking/`;

  /* ── Render ───────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* message */}
      {message && (
        <div className={`p-4 rounded-lg border text-sm ${
          message.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300'
            : 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* ── Enable & Mode ─────────────────────────────────── */}
      <div className="bg-card rounded-lg border border-border p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Online Booking</h3>
            <p className="text-sm text-muted-foreground mt-0.5">Allow patients to book appointments from your public page</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.is_enabled}
              onChange={(e) => setConfig((p) => ({ ...p, is_enabled: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white dark:after:bg-gray-300 after:border-gray-300 dark:after:border-gray-500 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary dark:bg-gray-600" />
          </label>
        </div>

        {config.is_enabled && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-sm break-all">
            <span className="text-muted-foreground">Public booking URL: </span>
            <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline">
              {bookingUrl}
            </a>
          </div>
        )}

        {/* Booking Mode */}
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-2">Booking Mode</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {BOOKING_MODES.map((m) => (
              <button
                key={m.value}
                onClick={() => setConfig((p) => ({ ...p, booking_mode: m.value as any }))}
                className={`text-left p-3 rounded-lg border-2 transition-colors ${
                  config.booking_mode === m.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/30'
                }`}
              >
                <p className="text-sm font-medium text-card-foreground">{m.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{m.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Slot Settings ─────────────────────────────────── */}
      <div className="bg-card rounded-lg border border-border p-6 space-y-4">
        <h3 className="text-base font-semibold text-card-foreground">Slot Settings</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Duration (min)</label>
            <input
              type="number"
              value={config.slot_duration}
              onChange={(e) => setConfig((p) => ({ ...p, slot_duration: Number(e.target.value) }))}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-card-foreground"
              min={5}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Max Days Ahead</label>
            <input
              type="number"
              value={config.max_advance_days}
              onChange={(e) => setConfig((p) => ({ ...p, max_advance_days: Number(e.target.value) }))}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-card-foreground"
              min={1}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Min Hours Notice</label>
            <input
              type="number"
              value={config.min_advance_hours}
              onChange={(e) => setConfig((p) => ({ ...p, min_advance_hours: Number(e.target.value) }))}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-card-foreground"
              min={0}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Buffer (min)</label>
            <input
              type="number"
              value={config.buffer_between_slots}
              onChange={(e) => setConfig((p) => ({ ...p, buffer_between_slots: Number(e.target.value) }))}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-card-foreground"
              min={0}
            />
          </div>
        </div>
      </div>

      {/* ── Fixed Weekly Schedule ──────────────────────────── */}
      {config.booking_mode === 'fixed_schedule' && (
        <div className="bg-card rounded-lg border border-border p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-card-foreground">Weekly Schedule</h3>
            <button
              onClick={addWeeklySlot}
              className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
            >
              + Add Slot
            </button>
          </div>
          {weeklySlots.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">No weekly slots configured. Click "Add Slot" to start.</p>
          )}
          <div className="space-y-2">
            {weeklySlots.map((slot, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-background">
                <select
                  value={slot.day_of_week}
                  onChange={(e) => updateWeeklySlot(idx, 'day_of_week', Number(e.target.value))}
                  className="rounded-lg border border-border bg-background px-2 py-1.5 text-sm text-card-foreground"
                >
                  {DAY_NAMES.map((d, i) => (
                    <option key={i} value={i}>{d}</option>
                  ))}
                </select>
                <input
                  type="time"
                  value={slot.start_time}
                  onChange={(e) => updateWeeklySlot(idx, 'start_time', e.target.value)}
                  className="rounded-lg border border-border bg-background px-2 py-1.5 text-sm text-card-foreground"
                />
                <span className="text-muted-foreground text-xs">to</span>
                <input
                  type="time"
                  value={slot.end_time}
                  onChange={(e) => updateWeeklySlot(idx, 'end_time', e.target.value)}
                  className="rounded-lg border border-border bg-background px-2 py-1.5 text-sm text-card-foreground"
                />
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer ml-auto">
                  <input
                    type="checkbox"
                    checked={slot.is_active}
                    onChange={(e) => updateWeeklySlot(idx, 'is_active', e.target.checked)}
                    className="rounded border-border"
                  />
                  Active
                </label>
                <button onClick={() => removeWeeklySlot(idx)} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 dark:text-red-400 transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Available Treatments ───────────────────────────── */}
      <div className="bg-card rounded-lg border border-border p-6 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-card-foreground">Available Treatments</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Select which treatments patients can book online. Leave empty = all treatments.</p>
        </div>
        {allTreatments.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No treatments found. Configure them in the Treatment Types tab first.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {allTreatments.map((t) => (
              <label key={t.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-border hover:bg-muted/30 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={selectedTreatmentIds.has(t.id)}
                  onChange={(e) => {
                    setSelectedTreatmentIds((prev) => {
                      const next = new Set(prev);
                      e.target.checked ? next.add(t.id) : next.delete(t.id);
                      return next;
                    });
                  }}
                  className="rounded border-border"
                />
                <span className="text-sm text-card-foreground">{t.name}</span>
                <span className="text-xs text-muted-foreground ml-auto">{t.duration} min</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* ── Patient Requirements ───────────────────────────── */}
      <div className="bg-card rounded-lg border border-border p-6 space-y-4">
        <h3 className="text-base font-semibold text-card-foreground">Patient Form</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.require_phone}
              onChange={(e) => setConfig((p) => ({ ...p, require_phone: e.target.checked }))}
              className="rounded border-border"
            />
            <span className="text-sm text-card-foreground">Require Phone</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.require_email}
              onChange={(e) => setConfig((p) => ({ ...p, require_email: e.target.checked }))}
              className="rounded border-border"
            />
            <span className="text-sm text-card-foreground">Require Email</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.auto_confirm}
              onChange={(e) => setConfig((p) => ({ ...p, auto_confirm: e.target.checked }))}
              className="rounded border-border"
            />
            <span className="text-sm text-card-foreground">Auto-confirm</span>
          </label>
        </div>
      </div>

      {/* ── Branding ───────────────────────────────────────── */}
      <div className="bg-card rounded-lg border border-border p-6 space-y-4">
        <h3 className="text-base font-semibold text-card-foreground">Booking Page Branding</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Page Title</label>
            <input
              type="text"
              value={config.page_title}
              onChange={(e) => setConfig((p) => ({ ...p, page_title: e.target.value }))}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-card-foreground"
              placeholder="e.g. Book Your Dental Appointment"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Primary Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={config.primary_color}
                onChange={(e) => setConfig((p) => ({ ...p, primary_color: e.target.value }))}
                className="w-10 h-10 rounded-lg border border-border cursor-pointer"
              />
              <input
                type="text"
                value={config.primary_color}
                onChange={(e) => setConfig((p) => ({ ...p, primary_color: e.target.value }))}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-card-foreground font-mono"
              />
            </div>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Welcome Message</label>
          <textarea
            value={config.welcome_message}
            onChange={(e) => setConfig((p) => ({ ...p, welcome_message: e.target.value }))}
            rows={2}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-card-foreground resize-none"
            placeholder="Welcome! Book your next appointment online."
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Booking Instructions</label>
          <textarea
            value={config.booking_instructions}
            onChange={(e) => setConfig((p) => ({ ...p, booking_instructions: e.target.value }))}
            rows={2}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-card-foreground resize-none"
            placeholder="Please fill in your details to confirm the appointment."
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Confirmation Message</label>
          <textarea
            value={config.confirmation_message}
            onChange={(e) => setConfig((p) => ({ ...p, confirmation_message: e.target.value }))}
            rows={2}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-card-foreground resize-none"
            placeholder="Thank you! Your appointment has been booked."
          />
        </div>
      </div>

      {/* ── Save Button ────────────────────────────────────── */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save Online Booking Settings'}
        </button>
      </div>
    </div>
  );
};

export default OnlineBookingSettings;
